import { countFace, type LiarsEvent, type LiarsView } from '@campfire/game-liars-dice';
import { h } from '../dom.ts';
import { name, type GameUi, type TableCtx } from '../games.ts';
import { cup, die, randomFace, tumbling } from './dice.ts';

const plural = (q: number, face: number) => `${q} × ${face}${q === 1 ? '' : 's'}`;

const eventText = (ctx: TableCtx, e: LiarsEvent | null, v: LiarsView): string => {
  if (!e) return `${name(ctx, v.turn)} ${v.turn === ctx.you ? 'open' : 'opens'} the bidding.`;
  if (e.kind === 'bid') return `${name(ctx, e.bid.seat)} bid ${plural(e.bid.qty, e.bid.face)}.`;
  const verdict = e.holds ? `the bid held (${e.count} found)` : `the bid was a bluff (only ${e.count})`;
  return `${name(ctx, e.caller)} called Liar on ${name(ctx, e.bid.seat)}'s ${plural(e.bid.qty, e.bid.face)}: ${verdict}. ${name(ctx, e.loser)} lose${e.loser === ctx.you ? '' : 's'} a die${e.eliminated ? ' and ' + (e.loser === ctx.you ? 'are' : 'is') + ' out' : ''}.`;
};

export const liarsDiceUi: GameUi = {
  id: 'liars-dice',
  privateHands: true,
  seatInfo: (ctx, s) => {
    const k = (ctx.msg.view as LiarsView).counts[s] ?? 0;
    return k === 0 ? 'out' : `${k} di${k === 1 ? 'e' : 'ce'}`;
  },
  render(ctx) {
    const v = ctx.msg.view as LiarsView;
    const done = v.result !== null;
    const prev = v.bid;
    // Default the picker to the cheapest legal raise.
    const minQty = prev ? (prev.face === 6 ? prev.qty + 1 : prev.qty) : 1;
    const bidSig = `${v.round}-${prev?.qty ?? 0}-${prev?.face ?? 0}`;
    if (ctx.local.get<string>('ld-sig') !== bidSig) {
      ctx.local.set('ld-sig', bidSig);
      ctx.local.set('ld-qty', minQty);
      ctx.local.set('ld-face', prev ? (prev.face === 6 ? 1 : prev.face + 1) : 2);
    }
    const qty = Math.min(ctx.local.get<number>('ld-qty') ?? minQty, v.total);
    const face = ctx.local.get<number>('ld-face') ?? 2;
    const legal = qty > (prev?.qty ?? 0) || (qty === (prev?.qty ?? 0) && face > (prev?.face ?? 0));
    const reveal = v.last?.kind === 'reveal' ? v.last : null;

    const cups = h(
      'div.ld-cups',
      { 'data-testid': 'ld-cups' },
      v.counts.map((k, s) =>
        h(
          `div.ld-seat${s === v.turn && !done ? '.active' : ''}${k === 0 ? '.out' : ''}`,
          { 'data-testid': `ld-seat-${s}` },
          h('div.other-name', {}, name(ctx, s)),
          k === 0 ? h('div.muted', {}, 'out') : cup(k, { testid: `ld-cup-${s}` }),
          prev && prev.seat === s ? h('div.ld-bid-chip', { 'data-testid': `ld-lastbid-${s}` }, plural(prev.qty, prev.face)) : null,
        ),
      ),
    );

    // Each round everyone shakes their cup: your new dice tumble before they settle (FR-DICE-01).
    const shaking = !!v.myDice && tumbling(ctx, 'ld-roll', `${v.round}-${v.myDice.join('')}`, true);
    const mine = v.myDice
      ? h(
          `div.dice-row${shaking ? '.rolling' : ''}`,
          { 'data-testid': 'ld-mine', 'data-rolling': shaking ? 'true' : undefined },
          v.myDice.map((d, i) => die(shaking ? randomFace() : d, { testid: `ld-die-${i}` })),
        )
      : null;

    const revealBox = reveal
      ? h(
          'div.ld-reveal',
          { 'data-testid': 'reveal' },
          reveal.dice.map((ds, s) =>
            ds.length
              ? h(
                  'div.ld-reveal-row',
                  {},
                  h('span.other-name', {}, name(ctx, s)),
                  h('div.dice-row.small', {}, ds.map((d) => die(d, { small: true, dim: d !== reveal.bid.face && !(v.wild && d === 1 && reveal.bid.face !== 1) }))),
                )
              : null,
          ),
        )
      : null;

    const picker =
      ctx.myTurn && !done
        ? h(
            'div.ld-picker',
            { 'data-testid': 'ld-picker' },
            h(
              'div.row.ld-qty',
              {},
              h('button', { 'data-testid': 'ld-qty-down', disabled: qty <= 1, onclick: () => ctx.local.set('ld-qty', Math.max(1, qty - 1)) }, '−'),
              h('strong', { 'data-testid': 'ld-qty' }, `${qty}`),
              h('button', { 'data-testid': 'ld-qty-up', disabled: qty >= v.total, onclick: () => ctx.local.set('ld-qty', Math.min(v.total, qty + 1)) }, '+'),
            ),
            h(
              'div.dice-row.small.ld-faces',
              {},
              [1, 2, 3, 4, 5, 6].map((f) => die(f, { selected: f === face, onclick: () => ctx.local.set('ld-face', f), testid: `ld-face-${f}` })),
            ),
            h(
              'div.row.actions',
              {},
              h('button.primary.big', { 'data-testid': 'ld-bid', disabled: !legal, onclick: () => ctx.act({ type: 'bid', qty, face }) }, legal ? `Bid ${plural(qty, face)}` : 'Raise the bid'),
              prev ? h('button.big', { 'data-testid': 'ld-liar', onclick: () => ctx.act({ type: 'liar' }) }, 'Liar!') : null,
            ),
          )
        : null;

    const odds = v.myDice && prev ? `You hold ${countFace([v.myDice], prev.face, v.wild)} of the ${prev.face}s.` : null;

    return h(
      'div.ld-table',
      {},
      h('div.muted', { 'data-testid': 'ld-info' }, `Round ${v.round} · ${v.total} dice in play · ${v.wild ? 'ones are wild' : 'ones are ordinary'}`),
      cups,
      prev ? h('div.ld-current', { 'data-testid': 'ld-current' }, `Current bid: ${plural(prev.qty, prev.face)} by ${name(ctx, prev.seat)}`) : null,
      h('div.event', { 'data-testid': 'event' }, eventText(ctx, v.last, v)),
      revealBox,
      mine,
      ctx.myTurn && odds ? h('div.muted', {}, odds) : null,
      picker,
    );
  },
};
